export type AppTheme = "light" | "dark" | "pink";

export const THEME_STORAGE_KEY = "diet-tracker-theme";

const THEME_COLORS: Record<AppTheme, string> = {
  light: "#12372a",
  dark: "#101713",
  pink: "#c21f78",
};

const THEME_SEQUENCE: readonly AppTheme[] = ["light", "dark", "pink"];

export function getPreferredTheme(): AppTheme {
  const storedTheme = readStoredTheme();
  if (storedTheme) return storedTheme;

  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function applyTheme(theme: AppTheme): void {
  if (typeof document === "undefined") return;

  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[theme]);
}

export function initializeTheme(): AppTheme {
  const theme = getPreferredTheme();
  applyTheme(theme);
  return theme;
}

export function saveTheme(theme: AppTheme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Theme still applies for this session when browser storage is unavailable.
  }
  applyTheme(theme);
}

export function getNextTheme(theme: AppTheme): AppTheme {
  const currentIndex = THEME_SEQUENCE.indexOf(theme);
  return THEME_SEQUENCE[(currentIndex + 1) % THEME_SEQUENCE.length];
}

function readStoredTheme(): AppTheme | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" || value === "pink" ? value : undefined;
  } catch {
    return undefined;
  }
}
